/**
 * Brand-logo helpers. Integration providers and client organizations are
 * rendered with their real, official brand icon, fetched by domain from a
 * keyless icon service (Google's favicon service — full-colour PNG), with a
 * graceful fallback (category icon / initials).
 *
 * Override the source by setting REACT_APP_LOGO_DEV_TOKEN (uses Logo.dev for
 * higher-resolution logos). No secrets are embedded; this is a public image URL.
 */
const LOGO_DEV_TOKEN = process.env.REACT_APP_LOGO_DEV_TOKEN;

// Canonical domains for the supported integration providers (favicon-grade brand icons).
export const PROVIDER_DOMAINS = {
  "Tukoro AI": "daakyi.com",
  "Cohere": "cohere.com",
  "Mistral": "mistral.ai",
  "AWS": "aws.amazon.com",
  // Deep cloud-connector display labels (Continuous Assurance) — must map so they
  // never fall back to a monogram in the Integrations Hub.
  "Amazon Web Services": "aws.amazon.com",
  "Microsoft 365": "microsoft.com",
  "aws": "aws.amazon.com",
  "m365": "microsoft.com",
  "azure": "azure.microsoft.com",
  "gcp": "cloud.google.com",
  "Microsoft Azure": "azure.microsoft.com",
  "Google Cloud": "cloud.google.com",
  "Sangfor HCI/SCP": "sangfor.com",
  "HubSpot": "hubspot.com",
  "Salesforce": "salesforce.com",
  "Zoho CRM": "zoho.com",
  "Pipedrive": "pipedrive.com",
  "Gmail": "google.com",
  "Microsoft 365 Outlook": "outlook.com",
  "SendGrid": "sendgrid.com",
  "Mailgun": "mailgun.com",
  "Google Drive": "drive.google.com",
  "SharePoint": "sharepoint.com",
  "OneDrive": "onedrive.live.com",
  "Box": "box.com",
  "Dropbox": "dropbox.com",
  "ServiceNow GRC/IRM": "servicenow.com",
  "Archer": "archerirm.com",
  "OneTrust": "onetrust.com",
  "Drata": "drata.com",
  "Vanta": "vanta.com",
  "Sprinto": "sprinto.com",
  "Jira": "atlassian.com",
  "ServiceNow": "servicenow.com",
  "Freshservice": "freshservice.com",
  "Zendesk": "zendesk.com",
  "Linear": "linear.app",
  "Microsoft Entra ID": "microsoft.com",
  "Okta": "okta.com",
  "Google Workspace": "workspace.google.com",
  "Auth0": "auth0.com",
  "Power BI": "powerbi.microsoft.com",
  "Tableau": "tableau.com",
  "Looker": "looker.com",
  "Metabase": "metabase.com",
};

/** Extract a clean host from a website/URL string. */
export function domainFromWebsite(website) {
  if (!website || typeof website !== "string") return null;
  let s = website.trim().toLowerCase();
  if (!s) return null;
  s = s.replace(/^https?:\/\//, "").replace(/^www\./, "");
  s = s.split("/")[0].split("?")[0].split("#")[0];
  // basic sanity: must contain a dot and no spaces
  if (!s.includes(".") || s.includes(" ")) return null;
  return s;
}

export function providerDomain(provider) {
  return PROVIDER_DOMAINS[provider] || null;
}

/** Logo image URL for a domain (keyless Google favicon, or Logo.dev when a token is set). */
export function logoUrlForDomain(domain) {
  if (!domain) return null;
  if (LOGO_DEV_TOKEN) {
    // True high-res, full-bleed official brand logos. retina=true renders at 2x for
    // crisp high-DPI display; fallback=404 lets our own category-icon/initials
    // fallback (in BrandLogo.jsx) take over rather than Logo.dev's plain monogram.
    return `https://img.logo.dev/${domain}?token=${LOGO_DEV_TOKEN}&size=128&format=png&retina=true&fallback=404`;
  }
  // Keyless fallback — Google's favicon service returns a full-colour, official
  // brand mark (PNG) and resolves cleanly for every enterprise domain in this catalog.
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
}

/**
 * Logo image URL resolved from a company NAME via Logo.dev's brand search
 * (e.g. "MTN Ghana", "GCB Bank"). Used for client orgs that have no website on
 * record so they still show a real brand logo instead of plain initials.
 * Requires a Logo.dev token (the keyless favicon service is domain-only); returns
 * null without one so the initials fallback is preserved. fallback=404 means an
 * unresolved name 404s → the caller's initials fallback takes over.
 */
export function logoUrlForName(name) {
  if (!name || !LOGO_DEV_TOKEN) return null;
  const q = encodeURIComponent(name.trim());
  if (!q) return null;
  return `https://img.logo.dev/name/${q}?token=${LOGO_DEV_TOKEN}&size=128&format=png&retina=true&fallback=404`;
}

/** Up-to-2-letter initials from a name, for avatar fallbacks. */
export function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Deterministic accent color for an initials avatar (stable per name). */
const AVATAR_COLORS = ["#1f3a8a", "#0f766e", "#9a3412", "#7c3aed", "#0e7490", "#a16207", "#b91c1c", "#4338ca"];
export function avatarColor(name) {
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
